<template>
  <div style="margin-top: 20px">
    <div class="inputs-actions">
      <v-text-field
        v-model="searchString"
        outlined
        dense
        class="inputs-actions__search"
        :disabled="loading"
        :label="$t('actions.search')"
        single-line
        append-icon="mdi-magnify"
        @input="debouncedFilterChange"
      />
      <plus-button
        copy="inputs.newPricingGroup"
        @clickFunction="() => (isCreateDialogOpen = true)"
      />
    </div>
    <create-pricing-group-tree
      v-if="isCreateDialogOpen"
      :is-open="isCreateDialogOpen"
      :mode="mode"
      :pricing-group-to-edit="pricingGroupToEdit"
      @closePricingGroupTreeDialog="closeCreateDialog"
    />
    <v-dialog
      :value="isDeleteDialogOpen"
      persistent
      max-width="40rem"
      @click:outside="closeDeleteDialog"
    >
      <v-card>
        <v-card-title class="header-text">
          {{ $t('dialogs.deleteHierarchy.header') }}
        </v-card-title>
        <v-card-text class="body-text">
          {{
            $t('dialogs.deleteHierarchy.confirm', {
              hierarchyName: get(pricingGroupToDelete, 'levelEntryDescription'),
            })
          }}
        </v-card-text>
        <v-card-actions class="btn-group">
          <v-spacer />
          <v-btn
            class="cancel-btn"
            outlined
            depressed
            :disabled="deleting"
            @click="closeDeleteDialog"
          >
            {{ $t('actions.cancel') }}
          </v-btn>
          <v-btn class="ok-btn" depressed :loading="deleting" @click="handleDelete">
            {{ $t('actions.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ag-grid-vue
      :style="{ width: '100%', height: `${dataTableHeight}px` }"
      class="ag-theme-inputsgrid"
      :loading="loading"
      :grid-options="gridOptions"
      :column-defs="columnDefs"
      :row-data="rowData"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { debounce, get, groupBy, isEmpty } from 'lodash';
import { architectureGroupLevel, pricingGroupLevel } from '@enums/hierarchy';
import dialogModes from '@enums/dialog-modes';
import inputScreensTableMixin from '../../../mixins/inputScreensTable';

import editButtonCellRenderer from './edit-button-cell-renderer.vue';
import deleteButtonCellRenderer from './delete-button-cell-renderer.vue';

export default {
  name: 'CDTHierarchies',
  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    editButtonCellRenderer,
    // eslint-disable-next-line vue/no-unused-components
    deleteButtonCellRenderer,
  },
  mixins: [inputScreensTableMixin],
  data() {
    return {
      searchString: '',
      isCreateDialogOpen: false,
      isDeleteDialogOpen: false,
      mode: dialogModes.create,
      deleting: false,
      pricingGroupToDelete: null,
      pricingGroupToEdit: null,
      gridOptions: {
        defaultColDef: {
          resizable: true,
          filter: 'agMultiColumnFilter',
          headerClass: 'ag-product-list-grid__header',
          minWidth: 50,
          flex: 1,
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
          suppressMovable: false,
        },
      },
      columnDefs: [
        {
          headerName: '',
          id: 'delete',
          colId: 'delete',
          suppressColumnsToolPanel: true,
          suppressHeaderMenuButton: true,
          pinned: 'left',
          maxWidth: 40,
          filter: false,
          sort: false,
          cellRendererSelector: params => {
            if (!isEmpty(params.data.cdtNodes)) {
              return { component: 'deleteButtonCellRenderer' };
            }
          },
          onCellClicked: this.openDeleteDialog,
        },
        {
          headerName: '',
          id: 'edit',
          colId: 'edit',
          suppressColumnsToolPanel: true,
          suppressHeaderMenuButton: true,
          pinned: 'left',
          maxWidth: 40,
          filter: false,
          sort: false,
          cellRendererSelector: params => {
            if (!isEmpty(params.data.cdtNodes)) {
              return { component: 'editButtonCellRenderer' };
            }
          },
          onCellClicked: this.openCreateDialog,
        },
        {
          field: 'levelEntryDescription',
          colId: 'pricingGroup',
          menuTabs: ['filterMenuTab'],
          headerName: this.$t('pricing.pricingGroup'),
          filter: 'agMultiColumnFilter',
          suppressHeaderMenuButton: false,
          editable: false,
          cellClass: 'bold-text',
        },
        {
          field: 'cdtNodes',
          colId: 'cdtNodes',
          menuTabs: ['filterMenuTab'],
          headerName: this.$t('pricing.cdtNodes'),
          filter: 'agMultiColumnFilter',
          suppressHeaderMenuButton: false,
          editable: false,
          cellClass: 'bold-text',
          valueGetter: params => {
            return (params.data.cdtNodes || [{ cdtDescription: '' }])
              .map(cdt => cdt.cdtDescription)
              .join(', ');
          },
        },
        {
          field: `architectureGroups`,
          colId: 'architectureGroups',
          menuTabs: ['filterMenuTab'],
          headerName: `${this.$tc(`hierarchy.hierarchyLevels.${architectureGroupLevel}`, 2)}`,
          filter: 'agMultiColumnFilter',
          suppressHeaderMenuButton: false,
          editable: false,
          cellClass: 'bold-text',
          valueGetter: params => {
            return (params.data.architectureGroups || [{ levelEntryDescription: '' }])
              .map(ag => ag.levelEntryDescription)
              .join(', ');
          },
        },
      ],
    };
  },
  computed: {
    ...mapState('hierarchy', ['loading']),
    ...mapGetters('hierarchy', ['getHierarchy']),
    architectureGroupsByPg() {
      const architectureGroups = this.getHierarchy({ level: architectureGroupLevel });
      return groupBy(architectureGroups, 'parentId');
    },
    rowData() {
      const pricingGroups = this.getHierarchy({ level: pricingGroupLevel });
      return pricingGroups.map(pg => {
        return {
          ...pg,
          architectureGroups: get(this.architectureGroupsByPg, pg.levelEntryKey) || [],
        };
      });
    },
  },
  async created() {
    await this.fetchHierarchy();
  },
  methods: {
    get,
    ...mapActions('hierarchy', ['deleteHierarchyBranchByPricingGroup', 'fetchHierarchy']),
    ...mapMutations('notifications', ['addNotification']),
    openCreateDialog(params) {
      if (isEmpty(params.data.cdtNodes)) {
        console.error('cannot edit without cdt nodes');
        return;
      }

      this.mode = dialogModes.edit;
      this.pricingGroupToEdit = params.data;
      this.isCreateDialogOpen = true;
    },
    closeCreateDialog() {
      // revert back to default create mode
      this.mode = dialogModes.create;
      this.pricingGroupToEdit = null;
      this.isCreateDialogOpen = false;
    },
    openDeleteDialog(params) {
      if (isEmpty(params.data.cdtNodes)) {
        console.error('cannot delete without cdt nodes');
        return;
      }

      this.pricingGroupToDelete = params.data;
      this.isDeleteDialogOpen = true;
    },
    closeDeleteDialog() {
      // close disabled while deleting. mainly for click:outside
      if (this.deleting) return;

      this.pricingGroupToDelete = null;
      this.isDeleteDialogOpen = false;
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.calculateDataTableHeight();
      this.gridApi.showLoadingOverlay();
    },
    debouncedFilterChange: debounce(function(newFilter) {
      this.gridApi.setQuickFilter(newFilter);
    }, 800),
    async handleDelete() {
      this.deleting = true;

      let jobStatus;
      let notificationPayload;
      try {
        await this.deleteHierarchyBranchByPricingGroup(this.pricingGroupToDelete);
        this.deleting = false;
        jobStatus = 'finished';
        notificationPayload = this.pricingGroupToDelete.levelEntryDescription;
        // close modal on success, keep open on error. can close with cancel.
        this.closeDeleteDialog();
      } catch (error) {
        console.error(error);
        jobStatus = 'failed';
      }

      this.deleting = false;
      this.addNotification({
        id: `${new Date()}deleteHierarchyBranch`,
        jobStatus,
        baseTranslation: 'notifications.deleteHierarchyBranch',
        notificationPayload: notificationPayload || this.$t('errors.generic'),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

// relies on inputs-grid style being unscoped
.inputs-actions {
  justify-content: left;
  &__search {
    max-width: 400px !important;
  }
}

.body-text {
  padding-top: 0.5rem;
  font-size: 1.5rem;
  color: black;
}

.v-card {
  .meta-errors {
    color: $vuetify-error-red;
  }

  .header-text {
    padding-top: 2rem;
    font-size: 2rem;
    color: black;
  }

  .btn-group.v-card__actions {
    .cancel-btn {
      color: $btn-cancel-color;
      border-color: $btn-cancel-color;
    }

    .ok-btn {
      color: $pricing-white;
      background-color: $btn-ok-color;
      border-color: $btn-ok-color;
    }
  }
}

.header-text {
  padding-top: 0.5rem;
  font-size: 2rem !important;
  color: black;
}

// TODO: investigate global styling, !important should not be required
// working in webtool/client/src/js/components/delete-hierarchy-level-dialog.vue
.v-btn {
  font-size: 1.5rem !important;
  &.cancel-btn.v-btn--disabled {
    color: $pricing-grey !important;
  }
}
</style>
